import { Injectable } from "@angular/core";
import { LogError } from "@memberjunction/core";
import * as dayjs from 'dayjs';

@Injectable({
    providedIn: 'root'
})
export class Utils {
    formatCurrency(amount: number): string {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(amount);
    }

    formatPercentage(value: number): string {
        if (value === null || value === undefined || isNaN(value)) return '0%';
        return `${value.toFixed(1)}%`;
    }

    formatFileSize(bytes: number): string {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }

    formatDate(dateString: string): string {
        if (!dateString) return '';

        try {
            const date = dayjs(dateString);
            if (!date.isValid()) {
                return '';
            }
            return date.format('MM/DD/YYYY');
        } catch (error) {
            console.error('Error formatting date:', error, 'Value:', dateString);
            return '';
        }
    }

    getFileIcon(contentType: string): string {
        const type = contentType || '';
        if (type.includes('pdf')) return '📄';
        if (type.includes('word') || type.includes('document')) return '📝';
        if (type.includes('excel') || type.includes('spreadsheet')) return '📊';
        if (type.includes('powerpoint') || type.includes('presentation')) return '📋';
        if (type.includes('image')) return '🖼️';
        if (type.includes('text')) return '📃';
        return '📎';
    }

    getCategoryIcon(category: string): string {
        const iconMap: { [key: string]: string } = {
            'General Documents': '📄',
            'Announcements': '📢',
            'Meeting Materials': '📅',
            'Financial Reports': '💰',
            'Personal': '👤',
            'Work': '💼',
            'Photos': '🖼️'
        };
        return iconMap[category] || '📄';
    }

    getCategoryDisplayName(categoryName: string): string {
        const categoryMap: { [key: string]: string } = {
            'general-documents': 'General Documents',
            'announcements': 'Announcements',
            'meeting-materials': 'Meeting Materials',
            'financial-reports': 'Financial Reports',
            'personal': 'Personal',
            'work': 'Work',
            'photos': 'Photos'
        };
        return categoryMap[categoryName] || categoryName;
    }

    // Announcement-specific utility functions
    getPriorityIcon(priority: string): string {
        const iconMap: { [key: string]: string } = {
            'low': '🔵 Low',
            'normal': '🟢 Normal',
            'high': '🟡 High',
            'urgent': '🔴 Urgent'
        };
        return iconMap[priority?.toLowerCase()] || '🟢';
    }

    getAnnouncementStatusBadge(item: { userAction?: string; isUnread?: boolean }): string {
        if (!item) return '🆕 New';
        
        // Use userAction property from flattened structure
        const userAction = item.userAction;
        
        switch (userAction) {
            case 'read': return '📖 Seen';
            case 'unread': return item.isUnread ? '🆕 New' : '📖 Seen';
            default: return item.isUnread ? '🆕 New' : '📖 Seen';
        }
    }

    getStatusBadge(status: string): string {
        const statusMap: { [key: string]: string } = {
            'draft': '📝 Draft',
            'published': '📢 Published',
            'scheduled': '⏰ Scheduled',
            'expired': '⏳ Expired'
        };
        return statusMap[status?.toLowerCase()] || status?.toUpperCase() || '';
    }

    formatEngagementRate(item: { totalTargets?: number; readCount?: number }): string {
        if (!item || !item.totalTargets || item.totalTargets === 0) return '0%';
        const rate = Math.round(((item.readCount || 0) / item.totalTargets) * 100);
        return `${rate}% (${item.readCount || 0}/${item.totalTargets})`;
    }

    formatRelativeDate(dateInput: string | Date): string {
        try {
            const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
            const now = new Date();
            const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
            
            if (diffInHours < 1) {
                return 'Just now';
            } else if (diffInHours < 24) {
                return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
            } else {
                const diffInDays = Math.floor(diffInHours / 24);
                if (diffInDays < 7) {
                    return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
                } else {
                    const formattedDate = dayjs(dateInput);
                    return formattedDate.isValid() ? formattedDate.format('MM/DD/YYYY') : 'Unknown';
                }
            }
        } catch {
            return 'Unknown';
        }
    }

    /**
     * Get client IP address using multiple detection methods
     */
    async getClientIP(): Promise<string> {
        try {
        // Method 1: Try WebRTC-based IP detection
        const webRtcIP = await this.getWebRTCIP();
        if (webRtcIP && webRtcIP !== 'unknown') {
            return webRtcIP;
        }

        // Method 2: Try third-party IP service
        const serviceIP = await this.getIPFromService();
        if (serviceIP && serviceIP !== 'unknown') {
            return serviceIP;
        }

        // Method 3: Fallback - check for forwarded headers (if available through proxy)
        const forwardedIP = this.getForwardedIP();
        if (forwardedIP && forwardedIP !== 'unknown') {
            return forwardedIP;
        }

        return 'unknown';
        } catch (error) {
        LogError('Failed to get client IP:', undefined, error);
        return 'unknown';
        }
    }

    /**
     * Get IP using WebRTC (most reliable for local networks)
     */
    private async getWebRTCIP(): Promise<string> {
        return new Promise((resolve) => {
        try {
            const rtc = new RTCPeerConnection({ iceServers: [] });
            let foundIP = false;
            
            rtc.createDataChannel('');
            rtc.createOffer().then(offer => rtc.setLocalDescription(offer));
            
            rtc.onicecandidate = (event) => {
            if (event.candidate && !foundIP) {
                const candidate = event.candidate.candidate;
                const ipMatch = candidate.match(/([0-9]{1,3}(\.[0-9]{1,3}){3}|[a-f0-9]{1,4}(:[a-f0-9]{1,4}){7})/);
                
                if (ipMatch && ipMatch[0]) {
                const ip = ipMatch[0];
                // Filter out local/private IPs if we want public IP only
                if (!ip.startsWith('192.168.') && 
                    !ip.startsWith('10.') && 
                    !ip.startsWith('172.') && 
                    !ip.startsWith('127.') &&
                    ip !== '0.0.0.0') {
                    foundIP = true;
                    rtc.close();
                    resolve(ip);
                    return;
                }
                // If no public IP found, use the first valid private IP
                if (!foundIP && ip !== '0.0.0.0') {
                    foundIP = true;
                    rtc.close();
                    resolve(ip);
                    return;
                }
                }
            }
            };
            
            // Timeout after 3 seconds
            setTimeout(() => {
            if (!foundIP) {
                rtc.close();
                resolve('unknown');
            }
            }, 3000);
            
        } catch (error) {
            LogError('WebRTC IP detection failed:', undefined, error);
            resolve('unknown');
        }
        });
    }

    /**
     * Get IP from third-party service as fallback
     */
    private async getIPFromService(): Promise<string> {
        try {
        // Use a reliable, fast IP service with proper timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
        
        const response = await fetch('https://api.ipify.org?format=json', {
            method: 'GET',
            signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        
        if (response.ok) {
            const data = await response.json();
            return data.ip || 'unknown';
        }
        
        return 'unknown';
        } catch (error) {
        // Try alternative service
        try {
            const controller2 = new AbortController();
            const timeoutId2 = setTimeout(() => controller2.abort(), 3000); // 3 second timeout
            
            const response = await fetch('https://ipapi.co/ip/', {
            method: 'GET',
            signal: controller2.signal
            });
            
            clearTimeout(timeoutId2);
            
            if (response.ok) {
            const ip = await response.text();
            return ip.trim() || 'unknown';
            }
        } catch (fallbackError) {
            LogError('Both IP services failed:', undefined, fallbackError);
        }
        
        return 'unknown';
        }
    }

    /**
     * Check for forwarded IP headers (limited browser access)
     */
    private getForwardedIP(): string {
        try {
        // In a browser environment, we can't directly access HTTP headers
        // This would work in a server-side environment
        // For now, return unknown since browsers don't expose these headers
        return 'unknown';
        } catch {
        return 'unknown';
        }
    }
}